import { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDonHangMuaTrongNuocSearchContext } from '../../context';
import { FormSchema, initialFormValues } from '../../schema';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { usePriceLookup } from './hooks/usePriceLookup';
import { AritoFormV2 } from '@/components/custom/erp';
import BottomBar from '../BottomBar';

import { DeliveryInfoTab } from './DeliveryInfoTab';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { useDetailRows } from './hooks';

import { FormMode } from '@/types/form';
import { DetailTab } from './DetailTab';
import { OtherTab } from './OtherTab';

interface FormDialogProps {
  formMode: FormMode;
  open: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const { transactionOptions } = useDonHangMuaTrongNuocSearchContext();
  const { state, actions } = useFormFieldState(initialData);
  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedCell,
    handleRowClick,
    handleCellClick,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange,
    setNonWritableFields,
    setPasteConfigs,
    isPasting,
    setPriceLookupHandler
  } = useDetailRows(initialData?.chi_tiet_data || []);
  const priceLookupHandler = usePriceLookup();
  useEffect(() => {
    setPriceLookupHandler(priceLookupHandler);
  }, [priceLookupHandler, setPriceLookupHandler]);
  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setRows,
    onAfterRepeat: (updatedRows, repeatedField, sourceRow) => {
      const fieldsThatRequireCalculation = ['gia_nt', 'so_luong'];

      if (fieldsThatRequireCalculation.includes(repeatedField)) {
        return updatedRows.map(row => {
          const sourceRowUuid = sourceRow?.uuid;
          if (row.uuid === sourceRowUuid) return row;

          const gia_nt = Number(row.gia_nt) || 0;
          const so_luong = Number(row.so_luong) || 1;

          const tien_nt = gia_nt * so_luong;

          const taxRate = row.ma_thue_data?.thue_suat || row.ma_thue_data?.tax_rate || 0;
          const thue_nt = row.ma_thue_data ? (tien_nt * taxRate) / 100 : row.thue_nt || 0;

          return {
            ...row,
            tien_nt: tien_nt || 0,
            thue_nt: thue_nt || 0
          };
        });
      }

      return updatedRows;
    }
  });

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open, setActiveTab]);

  const { t_so_luong, t_cp, t_cp_nt, t_tien, t_tien_nt, t_thue, t_thue_nt, t_tt, t_tt_nt } = useMemo(() => {
    return calculateTotals(rows);
  }, [rows]);

  const formInitialData = useMemo(
    () => ({
      ...initialFormValues,
      ...(initialData || {}),
      ong_ba: initialData?.ong_ba || initialData?.nguoi_lien_he || initialFormValues.ong_ba,
      ma_gd: initialData?.ma_gd || initialData?.giao_dich || initialData?.ma_gd_data?.uuid || initialFormValues.ma_gd
    }),
    [initialData]
  );

  const handleSubmit = (data: MuaHangRecord) => {
    const defaultMaGD =
      formMode === 'add'
        ? transactionOptions[0]?.value || initialFormValues.ma_gd || ''
        : initialData?.ma_gd ||
          initialData?.giao_dich ||
          initialData?.ma_gd_data?.uuid ||
          transactionOptions[0]?.value ||
          initialFormValues.ma_gd ||
          '';
    const submitData = {
      ...initialFormValues,
      ...data,
      ma_gd: data.ma_gd || defaultMaGD
    };
    const formData = transformFormData(
      submitData,
      state,
      rows,
      {
        t_so_luong,
        t_cp,
        t_cp_nt,
        t_tien,
        t_tien_nt,
        t_thue,
        t_thue_nt,
        t_tt,
        t_tt_nt
      },
      entityUnit,
      formMode
    );

    if (formMode === 'edit') {
      onSubmit?.({ ...formData });
    } else {
      onSubmit?.({ ...formData });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  if (formMode === 'add' && initialData) {
    initialData.status = '0';
  }

  const bodyTabs = useMemo(
    () =>
      activeTab === 'info'
        ? [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={rows}
                  selectedRowUuid={selectedRowUuid}
                  selectedCell={selectedCell}
                  onRowClick={handleRowClick}
                  onCellClick={handleCellClick}
                  onAddRow={handleAddRow}
                  onDeleteRow={handleDeleteRow}
                  onCopyRow={handleCopyRow}
                  onPasteRow={handlePasteRow}
                  onMoveRow={handleMoveRow}
                  onCellValueChange={handleCellValueChange}
                  handleUpdateNonWritableFields={setNonWritableFields}
                  handleUpdatePasteConfigs={setPasteConfigs}
                  onRepeatForRows={handleRepeatDetailRows}
                  constraints={state.khachHang?.account_data?.constraints ?? []}
                  isPasting={isPasting}
                />
              )
            },

            {
              id: 'delivery-info',
              label: 'Thông tin giao hàng',
              component: <DeliveryInfoTab formMode={formMode} formState={{ state, actions }} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        : undefined,
    [
      activeTab,
      actions,
      formMode,
      handleAddRow,
      handleCellClick,
      handleCellValueChange,
      handleCopyRow,
      handleDeleteRow,
      handleMoveRow,
      handlePasteRow,
      handleRepeatDetailRows,
      handleRowClick,
      isPasting,
      rows,
      selectedCell,
      selectedRowUuid,
      setNonWritableFields,
      setPasteConfigs,
      state
    ]
  );

  return (
    <AritoFormV2<MuaHangRecord>
      title={title}
      actionButtons={actionButtons}
      subTitle='Đơn hàng mua trong nước'
      mode={formMode}
      initialData={formInitialData}
      schema={FormSchema}
      className='h-[100dvh] max-h-[100dvh] overflow-hidden'
      onSubmit={handleSubmit}
      onClose={handleClose}
      headerFields={
        <div>
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-[#023e8a] data-[state=active]:bg-[#f0f5fa] data-[state=active]:text-[#023e8a] data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-[#023e8a] data-[state=active]:bg-[#f0f5fa] data-[state=active]:text-[#023e8a] data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <div className='flex items-center gap-2 px-3'>
                {formMode !== 'add' && (
                  <InfoDropdown
                    uuid={initialData?.uuid}
                    ma_ct={initialData?.chung_tu}
                    className='flex !h-6 !w-6 !min-w-0 items-center justify-center rounded-sm border-none bg-transparent !px-0 !py-0 text-2xl font-normal text-gray-950 shadow-none hover:bg-slate-200'
                  />
                )}
              </div>
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        </div>
      }
      tabs={bodyTabs}
      bottomBar={
        activeTab === 'info' && (
          <BottomBar
            tongSoLuong={t_so_luong}
            tongChiPhi={t_cp}
            tongTien={t_tien}
            tongThue={t_thue}
            tongThanhToan={t_tt}
          />
        )
      }
    />
  );
};

export default FormDialog;
